import { useAuth } from '@/contexts/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useLanguage } from '@/contexts/useLanguage';
import { Users, FileQuestion, Activity, Settings, TrendingUp, Database, Sun, Moon, Sunset, Calendar } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

import { createNetworkAwareOperation } from '@/lib/networkUtils';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const getTimeOfDay = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  const getTimeIcon = () => {
    const timeOfDay = getTimeOfDay();
    switch (timeOfDay) {
      case 'morning': return Sun;
      case 'afternoon': return Sun;
      case 'evening': return Moon;
      default: return Sun;
    }
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getGreeting = () => {
    const timeOfDay = getTimeOfDay();
    const firstName = user?.user_metadata?.first_name || user?.email?.split('@')[0] || 'Admin';
    return t('adminDashboard.greeting', { timeOfDay, name: firstName });
  };
  
  const [adminStats, setAdminStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalExams: 0,
    totalQuestions: 0,
    avgSessionTime: 0,
    systemUptime: '99.9%',
    totalSessions: 0,
    avgScore: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminStats = async () => {
      try {
        setLoading(true);

        await createNetworkAwareOperation(async () => {
          // Fetch real data from Supabase
          const { data: users, count: userCount } = await supabase
            .from('profiles')
            .select('*', { count: 'exact' });

          const { data: sessions } = await supabase
            .from('exam_sessions')
            .select('*');

          const { data: questions, count: questionCount } = await supabase
            .from('questions')
            .select('*', { count: 'exact' });

          // Calculate stats
          const totalSessions = sessions?.length || 0;
          const avgScore = sessions?.length 
            ? Math.round(sessions.reduce((sum, s) => sum + (s.score || 0), 0) / sessions.length)
            : 0;

          setAdminStats({
            totalUsers: userCount || 0,
            activeUsers: Math.round((userCount || 0) * 0.68),
            totalExams: 45, // Mock data
            totalQuestions: questionCount || 0,
            avgSessionTime: 42, // Mock data
            systemUptime: '99.9%',
            totalSessions,
            avgScore
          });
        }, 'fetch admin stats');

      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminStats();
  }, []);

  return (
    <DashboardLayout isAdmin={true}>
      <div className="space-y-6">
        {/* Welcome Header with Greeting */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {(() => {
              const TimeIcon = getTimeIcon();
              return <TimeIcon className="h-8 w-8 text-primary" />;
            })()}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{getGreeting()}</h1>
              <div className="flex items-center gap-2 text-sm md:text-base text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{formatDate()}</span>
              </div>
            </div>
          </div>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('adminDashboard.welcome')}
          </p>
        </div>

        {/* Admin Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <Card className="hover:shadow-lg transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("dashboard.totalUsers")}</CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminStats.totalUsers.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                +12% {t("admin.fromLastMonth")}
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("dashboard.activeUsers")}</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminStats.activeUsers}</div>
              <p className="text-xs text-muted-foreground">
                68% {t("admin.ofTotalUsers")}
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("admin.questionBank")}</CardTitle>
              <FileQuestion className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminStats.totalQuestions.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                +50 {t("admin.addedThisWeek")}
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("admin.activeSessions")}</CardTitle>
              <Activity className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminStats.totalSessions}</div>
              <p className="text-xs text-muted-foreground">
                {t("admin.currentLiveSessions")}
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("admin.systemUptime")}</CardTitle>
              <Database className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminStats.systemUptime}</div>
              <p className="text-xs text-muted-foreground">
                {t("admin.last30Days")}
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("admin.platformAverage")}</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminStats.avgScore}%</div>
              <p className="text-xs text-muted-foreground">
                {t("admin.overallTestScores")}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Admin Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Card className="cursor-pointer hover:shadow-lg transition-all duration-200">
            <CardHeader className="text-center">
              <Users className="h-8 w-8 text-primary mx-auto mb-2" />
              <CardTitle>{t("admin.userManagement")}</CardTitle>
              <CardDescription>{t("admin.userManagementDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center">
                {t("admin.userManagementText")}
              </p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-all duration-200">
            <CardHeader className="text-center">
              <FileQuestion className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <CardTitle>{t("admin.questions")}</CardTitle>
              <CardDescription>{t("admin.questionsDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center">
                {t("admin.questionsText")}
              </p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-all duration-200">
            <CardHeader className="text-center">
              <Activity className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <CardTitle>{t("admin.sessions")}</CardTitle>
              <CardDescription>{t("admin.sessionsDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center">
                {t("admin.sessionsText")}
              </p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-all duration-200">
            <CardHeader className="text-center">
              <Settings className="h-8 w-8 text-gray-600 mx-auto mb-2" />
              <CardTitle>{t("admin.settings")}</CardTitle>
              <CardDescription>{t("admin.settingsDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground text-center">
                {t("admin.settingsText")}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;